// Configurazione API con JWT management

import { WorkerQueryTransferModes } from "./consts";
import {
  decodeJSONBuffer,
  toJSONArrayBuffer,
  toJSONSharedBuffer,
} from "./encoding";
import {
  CommunicationChannel,
  ResponsePayload as ResponseData,
  WorkerQueryOptions,
  WorkerQueryResultMessagePayload,
  WorkerQueryTransferMode,
} from "./types";
import { WebWorkerQueryChunkedOptions } from "./webWorker";

export async function sendQueryResult<T extends string = string, D = any>(
  client: CommunicationChannel,
  payload: D,
  type: T,
  request: { id: string; type: string; timestamp: number },
  opts?: WorkerQueryOptions
) {
  if (client) {
    // console.debug("📊 Sending stats query result to client:", {
    //   type,
    //   requestId: request.id,
    //   payload,
    //   opts,
    // });
    sendResponse(
      client,
      {
        type,
        payload,
        request,
        timestamp: Date.now(),
      },
      opts
    );

    // console.debug("📊 Stats result sent to client:", {
    //   type,
    //   requestId: request.id,
    //   payload,
    //   opts,
    // });
  }
}

export function createChunksHandler<T>(
  resolve: (value: T) => void,
  reject?: (reason?: any) => void,
  opts?: WorkerQueryOptions
) {
  let chunks: T[] = [];
  let totalChunks = 0;
  let receivedChunks = 0;
  const id = Date.now();

  const handleInfo = (
    messageData: ResponseData<string, { totalChunks: number }>
  ) => {
    totalChunks = messageData.payload.totalChunks;
    console.log(`[${id}] Total chunks to receive:`, totalChunks);
  };

  const handleResult = (messageData: ResponseData<string, T[]>) => {
    const chunk = decodeJSONBuffer<T[]>(messageData.payload, {
      useCompression: opts?.response?.useCompression,
    });
    if (chunk !== null) {
      chunks = chunks.concat(chunk);
    }
    receivedChunks++;
  };

  return {
    onMessage: (data: ResponseData & { chunkIndex: number }) => {
      if (opts?.verbose) {
        console.log(`[${id}] Chunk message received:`, data, {
          totalChunks,
          receivedChunks,
        });
      }

      if (data.type == "chunk-info") {
        handleInfo(data);

        if (opts?.verbose) {
          console.log(`[${id}] Expecting ${totalChunks} chunks`);
        }
      } else if (data.type == "chunk-result") {
        handleResult(data);

        if (opts?.verbose) {
          console.log(
            `[${id}] Received chunk ${receivedChunks}/${totalChunks}`
          );
        }

        // Se abbiamo ricevuto tutti i chunk, risolvi
        if (data.chunkIndex + 1 === totalChunks) {
          if (opts?.verbose || true) {
            console.log(
              `[${id}] All chunks received, resolving with ${chunks.length} items`
            );
          }
          resolve(chunks as T);
        }
      } else {
        console.warn(
          `[${id}] Unknown chunk message type:`,
          data.type,
          ' expected one of [ "chunk-info", "chunk-result" ]'
        );
      }
    },
  };
}

type ResponseHandler = <T>(
  client: CommunicationChannel,
  data: ResponseData<string, T>,
  opts?: WorkerQueryOptions
) => void;

/**
 * Utility per worker: invia risposta in chunk usando SharedArrayBuffer per mantenere UI responsiva
 */

export const sendChunkedMessage: ResponseHandler = (
  client: CommunicationChannel,
  data: ResponseData,
  opts?: WorkerQueryOptions
) => {
  if (!client) {
    console.warn("No communication channel available to post chunked message.");
    return;
  } else if (!Array.isArray(data.payload)) {
    console.warn(
      "Data provided for chunked message is not an array:",
      data.payload
    );
    return;
  }

  const { chunkSize = 1000 } = opts?.response || {};

  // Calcola il numero totale di chunk
  const totalChunks = Math.ceil(data.payload.length / chunkSize);

  // console.log(
  //   `Sending ${data.payload.length} items in ${totalChunks} chunks of ${chunkSize}`
  // );

  // Invia prima le info sui chunk totali
  client.postMessage({
    type: "chunk-info",
    payload: { totalChunks },
    request: data.request,
    timestamp: Date.now(),
    opts,
  });

  // Invia ogni chunk con un piccolo delay per mantenere UI responsiva
  let currentChunk = 0;

  const sendNextChunk = () => {
    if (currentChunk >= totalChunks) return;

    const startIndex = currentChunk * chunkSize;
    const endIndex = Math.min(startIndex + chunkSize, data.payload.length);
    const chunk = data.payload.slice(startIndex, endIndex);

    client.postMessage({
      type: "chunk-result",
      payload: chunk,
      chunkIndex: currentChunk,
      request: data.request,
      timestamp: Date.now(),
      opts,
    });

    currentChunk++;
    // console.log(`Sent chunk ${currentChunk}/${totalChunks}`);

    // Schedula il prossimo chunk con un piccolo delay per UI responsiveness
    if (currentChunk < totalChunks) {
      setTimeout(sendNextChunk, 0);
    }
  };

  // Inizia l'invio dei chunk
  sendNextChunk();
};
/**
 * Utility per worker: invia risposta usando SharedArrayBuffer
 */

export const sendSharedBufferMessage: ResponseHandler = (
  client: CommunicationChannel,
  data: ResponseData,
  opts?: WorkerQueryOptions
) => {
  if (!client) {
    console.warn(
      "No communication channel available to post shared message:",
      data.type,
      data
    );
    return;
  }

  let payload: SharedArrayBuffer;
  if (!opts?.response?.skipEncoding) {
    payload = toJSONSharedBuffer(data.payload, {
      useCompression: opts?.response?.useCompression,
    });
  } else if (!(data.payload instanceof SharedArrayBuffer)) {
    throw new Error(
      "Data must be a SharedArrayBuffer when transferMode is SHARED_ARRAY_BUFFER and skipEncoding is true."
    );
  } else {
    payload = data.payload;
  }

  const message = { ...data, payload, opts };

  client.postMessage(message);
};

export const sendArrayBufferMessage: ResponseHandler = (
  client: CommunicationChannel,
  data: ResponseData,
  opts?: WorkerQueryOptions
) => {
  if (!client) {
    console.warn(
      "No communication channel available to post array buffer message:",
      data.type,
      data,
      opts
    );
    return;
  }

  let payload: ArrayBuffer;
  if (!opts?.response?.skipEncoding) {
    payload = toJSONArrayBuffer(data.payload, {
      useCompression: opts?.response?.useCompression,
    });
  } else if (!(data.payload instanceof ArrayBuffer)) {
    throw new Error(
      "Data must be an ArrayBuffer when transferMode is ARRAY_BUFFER and skipEncoding is true."
    );
  } else {
    payload = data.payload;
  }

  client.postMessage({ ...data, payload, opts }, { transfer: [payload] });
};

export const sendMessage: ResponseHandler = (
  client: CommunicationChannel,
  data: ResponseData,
  opts?: WorkerQueryOptions
) => {
  if (!client) {
    console.warn(
      "No communication channel available to send message:",
      data.type,
      data
    );
    return;
  }

  client.postMessage({ ...data, opts });
};

const sendByTransferModeMap: Record<WorkerQueryTransferMode, ResponseHandler> =
  {
    [WorkerQueryTransferModes.SHARED_ARRAY_BUFFER]: sendSharedBufferMessage,
    [WorkerQueryTransferModes.ARRAY_BUFFER]: sendArrayBufferMessage,
    [WorkerQueryTransferModes.CHUNKED]: sendChunkedMessage,
    [WorkerQueryTransferModes.DEFAULT]: sendMessage,
  };

export function sendResponse(
  client: CommunicationChannel,
  data: WorkerQueryResultMessagePayload,
  opts?: WorkerQueryOptions | WebWorkerQueryChunkedOptions
): void {
  const traceKey = opts?.traceKey || "anonymous query";

  if (!client) {
    console.warn(
      `[${traceKey}] No communication channel available to send response.`
    );
    return;
  }

  const transferMode =
    opts?.response?.transferMode || WorkerQueryTransferModes.DEFAULT;

  const send = sendByTransferModeMap[transferMode];

  send(client, data, opts);
}
